/**
 * Atlas PM · 项目类型 · 删除二次确认（P4.5）
 *
 * ⭐ 若后端返回 DICT_IN_USE（有项目在用），把后端中文消息**原样**展示，
 *    不吞掉引用数量（如「有 5 个项目正在使用该类型，无法删除」）。
 */

import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { ApiError } from '../../../api/client';
import type { ProjectTypeItem } from './api';
import { useDeleteProjectType } from './useProjectTypes';

export interface DeleteProjectTypeDialogProps {
  open: boolean;
  item: ProjectTypeItem | null;
  onClose(): void;
  onDeleted(id: string): void;
}

export default function DeleteProjectTypeDialog({
  open,
  item,
  onClose,
  onDeleted,
}: DeleteProjectTypeDialogProps) {
  const remove = useDeleteProjectType();
  const [error, setError] = useState<string | null>(null);

  const itemId = item?.id;

  useEffect(() => {
    if (!open) return;
    setError(null);
    remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  const confirm = () => {
    setError(null);
    remove.mutate(item.id, {
      onSuccess: () => {
        onDeleted(item.id);
        onClose();
      },
      onError: (err) =>
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : '删除失败',
        ),
    });
  };

  return (
    <div className="pt-modal-overlay" onClick={onClose}>
      <div
        className="pt-modal pt-modal--sm"
        role="dialog"
        aria-modal="true"
        data-testid="pt-delete-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-modal__head pt-modal__head--danger">
          <span className="pt-modal__title">删除项目类型 · {item.name}</span>
          <button
            type="button"
            className="pt-modal__close"
            aria-label="关闭"
            data-testid="pt-delete-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="pt-modal__body">
          <div className="pt-delete-meta">
            代码：<b className="num">{item.code}</b>
          </div>
          <div className="pt-delete-explain">
            删除后不可恢复。若已有项目正在使用该类型，系统会拒绝删除。
          </div>

          {error && (
            <div className="pt-form-error" data-testid="pt-delete-error">
              ⚠ {error}
            </div>
          )}

          <div className="pt-modal__foot">
            <Button
              type="button"
              variant="ghost"
              data-testid="pt-delete-cancel"
              onClick={onClose}
              disabled={remove.isPending}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="danger"
              data-testid="pt-delete-confirm"
              onClick={confirm}
              disabled={remove.isPending}
            >
              {remove.isPending ? '删除中…' : '确认删除'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
